import {createSearchParams, useParams} from 'react-router-dom'
import {getRequest, postRequest} from '../../../services/base'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {HarnessContext, SELECT_HARNESS, SET_HARNESS_LIST,} from './context/context'
import InputText from '../../common/InputText'
import HarnessTable from './HarnessTable'
import HarnessFormModal from './form'
import HarnessRelationsModal from './relation'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Flex, Form, Modal, Row} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const HarnessList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(HarnessContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/harnesses/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const [relationsHarness, setRelationsHarness] = useState(null)
  const [relationsModalOpen, setRelationsModalOpen] = useState(false)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    getHarnessListData()

    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.harnessList' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
  }, [])

  useEffect(() => {
    currentUrl.current = `/api/harnesses/?vehicle=${vehicle}&search=${search}`
    getHarnessListData()
  }, [debouncedSearch])

  const getHarnessListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'harnesses.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_HARNESS_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'harnesses.loadError' }))
    }
  }

  const buildUrl = ({ vehicle, search, limit, offset }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      limit,
      offset,
    })
    return `/api/harnesses/?${params.toString()}`
  }

  const onEdit = (record) => {
    dispatch({
      type: SELECT_HARNESS,
      data: { selected_harness: record, show_harness_popup: true },
    })
  }

  const onShowRelations = (record) => {
    setRelationsHarness(record)
    setRelationsModalOpen(true)
  }

  const deleteHarness = async (record) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'harnesses.deleting' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await postRequest(
      `/api/harnesses/harness_delete/${vehicle}/`,
      {
        harness_id: record.harness_id,
        vehicle: vehicle,
      }
    )
    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })
    if (res?.status !== false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'harnesses.deleteSuccess' }),
        },
      })
      getHarnessListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'harnesses.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'harnesses.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'harnesses.confirmDeleteContent' },
        { harness_id: record.harness_id }
      ),
      onOk: () => {
        deleteHarness(record)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.harness_list?.length) return []

    return state?.harness_list?.map((item, index) => ({
      ...item,
      key: `harness-${index}`,
      rowKey: `harness-${item.id}`,
      index: index,
    }))
  }, [state.harness_list])

  return (
    <>
      {contextHolder}

      <HarnessFormModal getHarnessListData={getHarnessListData} />

      <HarnessRelationsModal
        harness={relationsHarness}
        open={relationsModalOpen}
        onClose={() => {
          setRelationsModalOpen(false)
          setRelationsHarness(null)
        }}
      />

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" className="full-width">
              <InputText
                name="search_harness"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'harnesses.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-harness"
                onChange={(value) => {
                  setSearch(value)
                }}
              />
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <HarnessTable
              dataSource={dataSource}
              pagination={pagination}
              getHarnessListData={getHarnessListData}
              onEdit={onEdit}
              onDelete={confirmDelete}
              onShowRelations={onShowRelations}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default HarnessList
